import { useContext, useEffect, useMemo, useState } from 'react'
import DateFilterToolbar from 'Presentation/Components/CustomToolbar/DateFilter'

import { useLocation } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { UIContext } from 'Context/UI'
import { TableContext } from 'Context/Table'
import { useInquiryStore } from 'Store/Inquiry'

import Swal from 'sweetalert2'
import { shallow } from 'zustand/shallow'
import { OEMFilter } from 'Configuration/OEMFilter'
import { useLang } from '_metronic/i18n/Metronici18n'
import moment from 'moment'
import 'moment-timezone'

const INITIAL_FILTER_STATE: {
	startPeriod: string | null
	endPeriod: string | null
} = {
	startPeriod: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD HH:mm'),
	endPeriod: moment().format('YYYY-MM-DD HH:mm'),
}

const ViewModel = () => {
	const location = useLocation()
	const navigate = useNavigate()
	const lang = useLang()
	const { updateLoading, updateError } = useContext(TableContext)
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const { inquiries, stat, getInquiries, getStat } = useInquiryStore(
		state => ({
			inquiries: state.data,
			stat: state.stat,
			getInquiries: state.getAll,
			getStat: state.getStat,
		}),
		shallow
	)

	const [currentPage, setCurrentPage] = useState<number>(1)

	const [isLoading, setIsLoading] = useState(false)
	const [currentStatus, setCurrentStatus] = useState<string>('newRequest')
	const [isUseDateFilter, setIsUseDateFilter] = useState<boolean>(true)
	const [filter, setFilter] = useState(INITIAL_FILTER_STATE)
	const data = useMemo(() => {
		let _data: any[] = []

		inquiries.forEach(inquiry => {
			_data.push({
				type: 'IQ',
				...inquiry,
				items: inquiry?.oemRequest?.items,
			})
		})

		return _data.splice((currentPage - 1) * 10, 10)
	}, [inquiries, currentPage])

	const statusFilters = useMemo(
		() => [
			{
				// @ts-ignore
				label: OEMFilter['all'][lang],
				value: OEMFilter['all']['value'],
			},
			{
				// @ts-ignore
				label: OEMFilter['new'][lang],
				value: OEMFilter['new']['value'],
				number: stat?.newRequests,
			},
			{
				// @ts-ignore
				label: OEMFilter['send'][lang],
				value: OEMFilter['send']['value'],
				number: stat?.sentQuotation,
			},
			{
				// @ts-ignore
				label: OEMFilter['payment'][lang],
				value: OEMFilter['payment']['value'],
				number: stat?.paymentCompleted,
			},
		],
		[lang, stat]
	)

	const FILTER_OPTION_AVAILABLE = useMemo(
		() => [
			{
				label: lang === 'en' ? 'Inquiry Code' : 'หมายเลขคำสั่งซื้อ',
				value: 'inquiry',
			},
		],
		[lang]
	)

	const handleCurrentStatusFilterStateFromNavigation = () => {
		// @ts-ignore
		const status = location?.state?.status as any

		if (status) {
			setCurrentStatus(status)
		}
	}

	const onChangeFilterStatus = (status: string) => {
		setCurrentStatus(status)
	}

	const onChangeDateFilter = (
		type: 'today' | 'thisWeek' | 'thisMonth' | 'custom',
		key?: string,
		value?: any
	) => {
		if (type === 'today') {
			setFilter({
				startPeriod: moment()
					.tz('Asia/Bangkok')
					.set({
						hour: 0,
						minute: 0,
						second: 0,
						millisecond: 0,
					})
					.format('YYYY-MM-DD HH:mm'),
				endPeriod: moment()
					.tz('Asia/Bangkok')
					.set({
						hour: 23,
						minute: 59,
						second: 59,
						millisecond: 59,
					})
					.format('YYYY-MM-DD HH:mm'),
			})
		} else if (type === 'thisWeek') {
			// This week is start from Thrusday to Wednesday
			setFilter({
				startPeriod: moment()
					.tz('Asia/Bangkok')
					.startOf('week')
					.add(3, 'days')
					.set({
						hour: 0,
						minute: 0,
						second: 0,
						millisecond: 0,
					})
					.format('YYYY-MM-DD HH:mm'),
				endPeriod: moment()
					.tz('Asia/Bangkok')
					.endOf('week')
					.add(3, 'days')
					.set({
						hour: 23,
						minute: 59,
						second: 59,
						millisecond: 59,
					})
					.format('YYYY-MM-DD HH:mm'),
			})
		} else if (type === 'thisMonth') {
			setFilter({
				startPeriod: moment().tz('Asia/Bangkok').startOf('month').format('YYYY-MM-DD HH:mm'),
				endPeriod: moment().tz('Asia/Bangkok').endOf('month').format('YYYY-MM-DD HH:mm'),
			})
		} else if (type === 'custom') {
			if (key === 'startPeriod') {
				const finale =
					value !== null
						? moment(value)
								.tz('Asia/Bangkok')
								.set({
									hour: 0,
									minute: 0,
									second: 0,
									millisecond: 0,
								})
								.format('YYYY-MM-DD HH:mm HH:mm')
						: null

				setFilter({
					...filter,
					startPeriod: finale,
				})
			} else if (key === 'endPeriod') {
				const finale =
					value !== null
						? moment(value)
								.tz('Asia/Bangkok')
								.set({
									hour: 23,
									minute: 59,
									second: 59,
									millisecond: 59,
								})
								.format('YYYY-MM-DD HH:mm HH:mm')
						: null

				setFilter({
					...filter,
					endPeriod: finale,
				})
			}
		}
	}

	const onChangeFilter = (key: string, value: any) => {
		if (key === 'startPeriod' || key === 'endPeriod') {
			const finale =
				value !== null
					? moment(value)
							.tz('Asia/Bangkok')
							.set({
								hour: key === 'startPeriod' ? 0 : 23,
								minute: key === 'startPeriod' ? 0 : 59,
								second: key === 'startPeriod' ? 0 : 59,
								millisecond: key === 'startPeriod' ? 0 : 59,
							})
							.format('YYYY-MM-DD HH:mm')
					: null

			setFilter(prev => ({ ...prev, [key]: finale }))

			if (value !== null) {
				setIsUseDateFilter(false)
			}
		} else {
			setFilter(prev => ({ ...prev, [key]: value }))
		}
	}

	const fetchData = () => {
		if (filter.startPeriod === null || filter.endPeriod === null) return

		setIsLoading(true)
		updateLoading(true)
		updateError(false)

		Promise.all([getInquiries({ ...filter, status: currentStatus }), getStat()]).then(
			([
				{ success: inquirySuccess, data: inquiryData },
				{ success: statSuccess, data: statData },
			]) => {
				if (!inquirySuccess) {
					let title = ''
					let text = ''

					if (lang === 'en') {
						title = 'Failed to get order'
						text = inquiryData ?? 'Cannot fetch data'
					} else {
						title = 'ไม่สามารถดึงข้อมูลคำสั่งซื้อได้'
						text = inquiryData ?? 'ไม่สามารถดึงข้อมูล'
					}

					Swal.fire({
						title: title,
						text: text,
						icon: 'error',
						showConfirmButton: false,
						customClass: {
							confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						},
						timer: 1500,
					})

					updateError(true)
				} else if (!statSuccess) {
					let title = ''
					let text = ''

					if (lang === 'en') {
						title = "Failed to get order's statistic"
						text = inquiryData ?? 'Cannot fetch data'
					} else {
						title = 'ไม่สามารถดึงข้อมูลสถิติคำสั่งซื้อได้'
						text = inquiryData ?? 'ไม่สามารถดึงข้อมูล'
					}

					Swal.fire({
						title: title,
						text: text,
						icon: 'error',
						showConfirmButton: false,
						customClass: {
							confirmButton: 'btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white',
						},
						timer: 1500,
					})

					updateError(true)
				} else {
					setIsLoading(false)
					updateLoading(false)
					updateError(false)
				}
			}
		)
	}

	const viewDetail = (index: number) => {
		const request = data[index]
		if (request?.quotation && request?.quotation?.uuId) {
			navigate(`/oem/quotations/${request?.quotation?.uuId}`)
		} else {
			navigate(`/oem/inquiries/${request?.uuId}`)
		}
	}

	const goToPage = (page: number | null) => {
		if (page) {
			setCurrentPage(page)
			document.getElementById('request-list-anchor')?.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			})
		}
	}

	useEffect(() => {
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStatus, filter.startPeriod, filter.endPeriod])

	useEffect(() => {
		setShowContentToolbar(true)
		setCustomContentToolbar(
			<DateFilterToolbar
				isUseDateFilter={isUseDateFilter}
				onChangeDateFilter={onChangeDateFilter}
			/>
		)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter.startPeriod, filter.endPeriod, isUseDateFilter])

	useEffect(() => {
		handleCurrentStatusFilterStateFromNavigation()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		isLoading,
		statusFilters: statusFilters.map(status => ({
			label: status.label,
			isActive: currentStatus === status.value,
			value: status.value,
			number: status.number,
			numberColor: 'green' as any,
		})),
		currentPage,
		data,
		totalData: inquiries.length,
		viewDetail,
		goToPage,
		onChangeFilterStatus,
		filter,
		onChangeFilter,
		FILTER_OPTION_AVAILABLE,
	}
}

export default ViewModel
